import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GoogleLogo, Eye, EyeSlash, ArrowLeft, Fingerprint } from "@phosphor-icons/react";
import { loginWithPasskey, hasConditionalMediation, hasPlatformAuthenticator } from "@/lib/webauthn";

const LOGIN_BG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";
const PASSKEY_FLAG = "ht_passkey_last_used";

export default function Login() {
  const { user, loading: authLoading, login, complete2FA, googleLogin, applyPasskeySession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [twoFAStep, setTwoFAStep] = useState(null); // { interim_token } | null
  const [twoFACode, setTwoFACode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false); // trigger hint Google dopo 401
  // Il pulsante "Accedi con Passkey" primario appare solo se l'utente ha già usato
  // una passkey su questo device (flag localStorage impostato dopo attivazione/uso).
  const [passkeyKnown] = useState(() => {
    try { return localStorage.getItem(PASSKEY_FLAG) === "1"; } catch { return false; }
  });
  const autofillAbortRef = useRef(null);
  const autoPasskeyRef = useRef(false);

  const onPasskeySuccess = (data) => {
    const u = applyPasskeySession(data);
    toast.success(`Benvenuto ${u.first_name || u.email}`);
    if (u.needs_onboarding) nav("/onboarding", { replace: true });
    else nav("/dashboard");
  };

  // Conditional Mediation: se l'utente ha già usato una passkey su questo device
  // e il browser supporta la UI di autofill, avviamo silenziosamente il flusso.
  // Il prompt del sistema apparirà quando l'utente clicca sul campo email o sulla
  // proposta di autofill.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (twoFAStep) return;
        if (!(await hasConditionalMediation())) return;
        if (!passkeyKnown) return;
        // Avvia il flusso in background: il browser mostrerà l'account nel dropdown
        // dell'email; risolve solo quando l'utente lo seleziona.
        const data = await loginWithPasskey(null, { useBrowserAutofill: true });
        if (cancelled) return;
        onPasskeySuccess(data);
      } catch (err) {
        // NotAllowedError / AbortError = utente ha ignorato o annullato: silente.
        if (process.env.NODE_ENV !== "production") {
          console.debug("[login] passkey autofill skipped:", err?.name || err?.message);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoFAStep, passkeyKnown]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r && r.two_fa_required) {
        setTwoFAStep({ interim_token: r.interim_token });
        toast.info("Inserisci il codice a 6 cifre dall'app Authenticator");
        return;
      }
      setLoginFailed(false);
      toast.success(`Benvenuto ${r.first_name || r.email}`);
      if (r.needs_onboarding) nav("/onboarding", { replace: true });
      else nav("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore di accesso";
      if (err?.response?.status === 403) nav("/pending");
      else {
        if (err?.response?.status === 401) setLoginFailed(true);
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await complete2FA(twoFAStep.interim_token, twoFACode.trim(), useBackup);
      toast.success(`Benvenuto ${u.first_name || u.email}`);
      if (u.needs_onboarding) nav("/onboarding", { replace: true });
      else nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally { setLoading(false); }
  };

  const doPasskeyLogin = async ({ silent = false } = {}) => {
    setPasskeyLoading(true);
    try {
      // Passiamo l'email SOLO se digitata dall'utente (permette al server di
      // restringere gli allowCredentials). Altrimenti resta discoverable.
      const data = await loginWithPasskey(email?.trim() || null);
      onPasskeySuccess(data);
    } catch (err) {
      if (silent) return;
      if (err?.name === "NotAllowedError") {
        toast("Accesso Passkey annullato.");
      } else {
        const msg = err?.response?.data?.detail || err?.message || "Errore Passkey";
        toast.error(msg);
      }
    } finally { setPasskeyLoading(false); }
  };

  // PWA installata (standalone) con biometria disponibile: proponi subito la passkey.
  useEffect(() => {
    if (autoPasskeyRef.current || authLoading || user || twoFAStep) return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (!standalone) return;
    autoPasskeyRef.current = true;
    hasPlatformAuthenticator().then((ok) => { if (ok) doPasskeyLogin({ silent: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, twoFAStep]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => googleLogin();

  if (!authLoading && user) return <Navigate to={user.needs_onboarding ? "/onboarding" : "/dashboard"} replace />;

  return (
    <div className="split-auth">
      <div className="flex items-center justify-center p-4 sm:p-8 md:p-12 relative min-h-screen">
        <Link
          to="/"
          className="absolute top-4 left-4 md:top-6 md:left-6 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-purple-700 transition-colors"
          data-testid="login-back-home"
        >
          <ArrowLeft size={14} weight="bold" />
          Torna alla home
        </Link>
        <form
          onSubmit={twoFAStep ? submit2FA : submit}
          className="w-[90vw] max-w-[400px] md:w-full md:max-w-md fade-up login-form-responsive"
          data-testid="login-form"
        >
          <div className="mb-8 flex items-center gap-3">
            <img src="/logo.png" alt="Housetag" className="w-14 h-14 object-contain drop-shadow-md" />
            <div>
              <div className="font-display text-3xl font-bold tracking-tight leading-none">
                <span className="ht-gradient-text">Housetag.it</span>
              </div>
              <div className="label-eyebrow mt-2">Real Estate Suite</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold tracking-tight mb-2">Bentornato</h1>
          <p className="text-sm text-muted-foreground mb-8">
            La gestione dei tuoi immobili, semplice e digitale.
          </p>

          {/* Passkey primaria: mostrata solo se l'utente ha già registrato/usato
              una passkey su questo device (flag localStorage). */}
          {!twoFAStep && (
            <div className="mb-6 fade-up">
              <Button
                type="button"
                onClick={() => doPasskeyLogin()}
                disabled={passkeyLoading || loading}
                className="w-full ht-gradient border-0 h-11"
                data-testid="passkey-login-primary"
              >
                <Fingerprint size={18} weight="bold" className="mr-2" />
                {passkeyLoading ? "Verifica passkey…" : "Accedi con Passkey"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center mt-2">
                Impronta digitale, Face ID o PIN del dispositivo.
              </p>
            </div>
          )}

          <div className="space-y-4">
            {!twoFAStep && (
              <>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    data-testid="login-email"
                    placeholder="mario.rossi@studio.it"
                    autoComplete="username webauthn"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Password</Label>
                    <Link to="/forgot-password" className="text-xs text-purple-700 hover:underline" data-testid="forgot-link">Password dimenticata?</Link>
                  </div>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} required value={password} onChange={e=>setPassword(e.target.value)}
                      data-testid="login-password" placeholder="••••••••" className="pr-10" />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                      aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                      data-testid="toggle-login-password">
                      {showPassword ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {twoFAStep && (
              <div data-testid="twofa-step" className="space-y-3">
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-sm">
                  <div className="font-semibold text-purple-900 mb-1">Verifica in 2 passaggi</div>
                  <p className="text-xs text-purple-800">
                    {useBackup
                      ? "Inserisci uno dei codici di backup che hai salvato durante il setup."
                      : "Apri Google Authenticator (o Authy) e digita il codice a 6 cifre generato per Housetag."}
                  </p>
                </div>
                <div>
                  <Label>{useBackup ? "Codice di backup" : "Codice a 6 cifre"}</Label>
                  <Input
                    value={twoFACode}
                    onChange={e => setTwoFACode(e.target.value)}
                    placeholder={useBackup ? "es. a1b2c3d4" : "123456"}
                    inputMode={useBackup ? "text" : "numeric"}
                    autoFocus
                    className="font-mono text-lg tracking-widest text-center"
                    data-testid="twofa-code-input"
                  />
                </div>
                <button type="button" onClick={() => { setUseBackup(v => !v); setTwoFACode(""); }}
                  className="text-xs text-purple-700 hover:underline" data-testid="twofa-toggle-backup">
                  {useBackup ? "Torna al codice a 6 cifre" : "Non ho il codice — uso codice di backup"}
                </button>
                <button type="button" onClick={() => { setTwoFAStep(null); setTwoFACode(""); }}
                  className="block text-xs text-slate-500 hover:underline mt-1" data-testid="twofa-back">
                  ← Torna al login
                </button>
              </div>
            )}

            <Button type="submit" disabled={loading || (twoFAStep && !twoFACode)}
              className="w-full ht-gradient border-0"
              data-testid="login-submit">
              {loading ? "Accesso..." : (twoFAStep ? "Verifica codice" : "Accedi")}
            </Button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground tracking-widest">Oppure</span>
            </div>
          </div>

          {loginFailed && !twoFAStep && (
            <div
              className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-2 fade-up"
              data-testid="login-google-hint"
            >
              <svg viewBox="0 0 24 24" width={16} height={16} className="mt-0.5 shrink-0" aria-hidden="true">
                <path fill="#4285F4" d="M23.49 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.44c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.11C3.26 21.3 7.31 24 12 24z" />
                <path fill="#FBBC05" d="M5.27 14.26c-.24-.72-.38-1.49-.38-2.26s.14-1.54.38-2.26V6.63H1.28C.46 8.24 0 10.06 0 12s.46 3.76 1.28 5.37l3.99-3.11z" />
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.94 1.15 15.23 0 12 0 7.31 0 3.26 2.7 1.28 6.63l3.99 3.11C6.22 6.86 8.87 4.75 12 4.75z" />
              </svg>
              <div>
                <strong>Hai registrato l'account con Google?</strong> Non hai una password interna — clicca il pulsante <strong>"Accedi con Google"</strong> qui sotto.
              </div>
            </div>
          )}

          <Button type="button" variant="outline" onClick={startGoogle}
            className="w-full" data-testid="google-login">
            <GoogleLogo size={18} weight="bold" className="mr-2" />
            Accedi con Google
          </Button>

          <p className="text-sm text-center mt-8 text-muted-foreground">
            Non hai un account?{" "}
            <Link to="/register" className="ht-gradient-text font-semibold hover:underline" data-testid="link-register">
              Registrati
            </Link>
          </p>
        </form>
      </div>

      <div className="auth-image hidden md:block" style={{ backgroundImage: `url(${LOGIN_BG})` }}>
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="label-eyebrow text-white/80">Ecosistema digitale</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3">
              La gestione dei tuoi immobili, semplice e digitale.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              Gestionale, marketplace di servizi tecnici, workflow operativo.
              Progettato per professionisti del settore immobiliare.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
